/**
 * Cycle detail page - dynamic candidates list
 *
 * Embeds a candidate browse/search control filtered to candidates
 * for a specific election cycle. Reads cycle year from data attributes
 * on the #cycle-candidates container element.
 */

import { EntityIndex } from './base';
import { canonicalPath } from '@electinfo/common';
import type { EntityConfig, EntitySearchResult } from './types';

class CycleDetailCandidates extends EntityIndex {
  private cycleFilter = '';
  private cycleYear = '';

  protected setup(): void {
    const container = document.getElementById('cycle-candidates');
    if (!container) return;

    this.cycleYear = container.dataset.cycleYear || '';
    if (this.cycleYear) {
      // Use ~ prefix for CONTAINS matching on comma-separated active_cycles field
      this.cycleFilter = `active_cycles:~${this.cycleYear}`;
    }

    super.setup();
  }

  getConfig(): EntityConfig {
    return {
      entityType: 'Politician',
      placeholder: `Search candidates in the ${this.cycleYear} cycle...`,
      browseTitle: 'Candidates',
      sortField: 'total_contributions',
      sortOrder: 'DESC',
      fixedFilter: this.cycleFilter,
      tabs: [
        { id: 'all', label: 'All', filter: null },
        { id: 'president', label: 'President', filter: 'office:P' },
        { id: 'senate', label: 'Senate', filter: 'office:S' },
        { id: 'house', label: 'House', filter: 'office:H' }
      ],
      formatResult: (result: EntitySearchResult) => {
        const officeMap: Record<string, string> = { 'P': 'President', 'S': 'Senate', 'H': 'House' };
        const office = officeMap[result.office || ''] || result.office || '';
        const state = result.state || '';
        const district = result.district || '';
        const location = result.office === 'P' ? '' : ` - ${state}${district ? '-' + district : ''}`;
        const party = result.party || 'Unknown Party';

        let contributions = '';
        if (result.metadata) {
          try {
            const meta = JSON.parse(result.metadata);
            if (meta.total_contributions > 0) {
              contributions = ` | $${Math.round(meta.total_contributions).toLocaleString()}`;
            }
          } catch { /* ignore parse errors */ }
        }

        return {
          url: `/${canonicalPath('Politician', result.id)}/`,
          name: result.name || '',
          meta: `${office}${location} | ${party}${contributions}`
        };
      }
    };
  }

  /**
   * Insert the search UI into the #cycle-candidates container
   * instead of the default location (after header).
   */
  protected insertSearchUI(): void {
    const config = this.getConfig();
    const targetContainer = document.getElementById('cycle-candidates');
    if (!targetContainer) return;

    // Build tabs HTML
    let tabsHtml = '';
    if (config.tabs && config.tabs.length > 0) {
      const tabButtons = config.tabs.map((tab, index) =>
        `<button type="button" class="entity-filter-tab${index === 0 ? ' active' : ''}" data-filter="${tab.filter || ''}" data-tab-id="${tab.id}">${tab.label}</button>`
      ).join('');
      tabsHtml = `<div class="entity-filter-tabs" id="entity-filter-tabs">${tabButtons}</div>`;
    }

    const container = document.createElement('div');
    container.className = 'entity-search-container';
    container.innerHTML = `
      <form class="entity-search-form" id="entity-search-form">
        <input type="text" id="entity-search-input" placeholder="${config.placeholder}" autocomplete="off">
        <button type="submit">Search</button>
      </form>
      ${tabsHtml}
      <div class="entity-search-results" id="entity-search-results">
        <div class="entity-search-results-header">
          <h3 id="entity-results-title">${config.browseTitle}</h3>
          <button type="button" class="entity-search-clear" id="entity-search-clear" style="display: none;">Show All</button>
        </div>
        <div id="entity-search-results-list"></div>
        <div id="entity-pagination"></div>
      </div>
    `;

    targetContainer.appendChild(container);
  }
}

new CycleDetailCandidates();
